import { createSelector } from 'reselect';
import { produce } from 'immer';
import { set, uniqBy } from 'lodash-es';
import { ScopedType, ScopedAsyncType } from './scoped-type';
import * as service from '../../services/soil-sample-order-service';
import { asyncAction } from './async-action';
import { getAgronomists } from '../../services/user-service';
import {
  CANCELLED,
  COMPLETE,
  INTERNAL_SAMPLER,
  SUBMITTED,
} from '../../sections/soil-sampling/constants';

const NULL_MAP = {};
const Type = ScopedType('SOIL_SAMPLE_ORDER');
const AsyncType = ScopedAsyncType('SOIL_SAMPLE_ORDER');
const SLICE = 'soilSampleOrder';

const CLEAR_FAILURE = Type('CLEAR_FAILURE');
const ADD_ORDER = Type('ADD_ORDER');
const APPROVE_ORDERS = AsyncType('APPROVE_ORDERS');
const UNAPPROVE_ORDERS = AsyncType('UNAPPROVE_ORDERS');
const FINALIZE_ORDERS = AsyncType('FINALIZE_ORDERS');
const CANCEL_EDIT_ORDER = Type('CANCEL_EDIT_ORDER');
const CHANGE_NEW_ORDER = Type('CHANGE_NEW_ORDER');
const CHANGE_NEW_ORDER_FIELD = Type('CHANGE_NEW_ORDER_FIELD');
const CHANGE_ORDER = Type('CHANGE_ORDER');
const CHANGE_SAMPLERS = Type('CHANGE_SAMPLERS');
const DELETE_ORDER = AsyncType('DELETE_ORDER');
const EDIT_ORDER = Type('EDIT_ORDER');
const LOAD_DOMAIN_DATA = AsyncType('LOAD_DOMAIN_DATA');
const LOAD_FIELDS = AsyncType('LOAD_FIELDS');
const LOAD_ORDER = AsyncType('LOAD_ORDER');
const LOAD_ORDERS = AsyncType('LOAD_ORDERS');
const SET_ORDERS = Type('SET_ORDERS');
const UNLOAD_ORDERS = Type('UNLOAD_ORDERS');
const LOAD_AGRONOMISTS = AsyncType('LOAD_AGRONOMISTS');
const LOAD_COMPLETED_ORDERS = AsyncType('LOAD_COMPLETED_ORDERS');
const REMOVE_NEW_ORDER_FIELD = Type('REMOVE_NEW_ORDER_FIELD');
const CANCEL_NEW_ORDER = Type('CANCEL_NEW_ORDER');
const SAVE_NEW_ORDER = AsyncType('SAVE_NEW_ORDER');
const SAVE_ORDER = AsyncType('SAVE_ORDER');
const SET_FIELDS_SELECTED = Type('SET_FIELDS_SELECTED');
const SET_ORDERS_SELECTED = Type('SET_ORDERS_SELECTED');
const SET_ORDER_READY = AsyncType('SET_ORDER_READY');
const UPDATE_POINT_COUNT = Type('UPDATE_POINT_COUNT');

export const selectors = {
  agronomists: selectAgronomists,
  deleteHasFailed: selectDeleteHasFailed,
  draftEdit: selectDraftEdit,
  draftOrder: selectDraftOrder,
  editHasFailed: selectEditHasFailed,
  editIsWorking: selectEditIsWorking,
  editPointsHasFailed: selectEditPointsHasFailed,
  editPointsIsWorking: selectEditPointsIsWorking,
  fieldSpecialists: createSelectFieldSpecialists(),
  fields: createSelectFields(),
  findOrder: selectFindOrder,
  hasActiveOrder: createHasActiveOrder(),
  hasFailed: selectHasFailed,
  hasSubmittedOrder: createHasSubmittedOrder(),
  isWorking: selectIsWorking,
  isLoading: selectIsLoading,
  laboratories: selectLaboratories,
  map: selectMap,
  newOrderHasCompleted: newOrderIs('complete'),
  newOrderHasFailed: newOrderIs('error'),
  newOrderIsWorking: newOrderIs('working'),
  orders: selectOrders,
  dashboardStats: selectDashboardStats,
  points: selectPoints,
  priorities: selectPriorities,
  contractTypes: selectContractTypes,
  samplers: selectSamplers,
  seasons: selectSeasons,
  scheduledFilter: selectScheduledFilter,
  selectedFields: createSelectSelectedFields(),
  selectedOrders: createSelectSelectedOrders(),
  selectedOrderIds: selectSelectOrderIds,
};

export const actions = {
  addOrder,
  approveOrders,
  cancelNewOrder,
  cancelOrderEdit,
  changeOrder,
  changeSamplers,
  clearFailure,
  createOrders,
  deleteOrder,
  editOrder,
  finalizeOrders,
  loadAgronomists,
  loadAllOrdersForGrower,
  loadActiveOrders,
  loadUrgentOrders,
  loadCompletedOrders,
  loadDomainData,
  loadFields,
  reloadOrder,
  removeFieldFromOrder,
  saveOrder,
  setFieldsSelected,
  setNewOrderData,
  setNewOrderFieldData,
  setOrderReadyToSample,
  setOrdersSelected,
  setOrders,
  unapproveOrders,
  unloadOrders,
  updatePointCount,
};

const reducerHandlers = {
  [CLEAR_FAILURE]: reduceClearFailure,
  [CANCEL_NEW_ORDER]: reduceCancelNewOrder,
  [SAVE_NEW_ORDER.start]: reduceSaveNewOrderStart,
  [SAVE_NEW_ORDER.complete]: reduceSaveNewOrderComplete,
  [SAVE_NEW_ORDER.failure]: reduceSaveNewOrderFailure,
  [ADD_ORDER]: reduceStartNewOrder,
  [REMOVE_NEW_ORDER_FIELD]: reduceRemoveNewOrderField,
  [CHANGE_NEW_ORDER]: reduceChangeNewOrder,
  [CHANGE_NEW_ORDER_FIELD]: reduceChangeNewOrderField,
  [LOAD_DOMAIN_DATA.complete]: reduceLoadDomainDataComplete,
  [LOAD_FIELDS.complete]: reduceLoadFieldsComplete,
  [LOAD_FIELDS.start]: reduceLoadFieldsStart,
  [LOAD_AGRONOMISTS.complete]: reduceLoadAgronomistsComplete,
  [LOAD_AGRONOMISTS.start]: reduceLoadAgronomistsStart,
  [UNLOAD_ORDERS]: reduceUnloadOrders,
  [SET_FIELDS_SELECTED]: reduceSetSelectedFields,
  [LOAD_ORDER.start]: reduceLoadOrderStart,
  [LOAD_ORDER.complete]: reduceLoadOrderComplete,
  [LOAD_ORDER.failure]: reduceLoadOrderFailure,
  [LOAD_COMPLETED_ORDERS.start]: reduceLoadCompletedOrdersStart,
  [LOAD_COMPLETED_ORDERS.complete]: reduceLoadCompletedOrdersComplete,
  [LOAD_COMPLETED_ORDERS.failure]: reduceLoadCompletedOrdersFailure,
  [LOAD_ORDERS.start]: reduceLoadOrdersStart,
  [LOAD_ORDERS.complete]: reduceLoadOrdersComplete,
  [LOAD_ORDERS.failure]: reduceLoadOrdersFailure,
  [SET_ORDERS]: reduceSetOrders,
  [SET_ORDERS_SELECTED]: reduceSetSelectedOrders,
  [CANCEL_EDIT_ORDER]: reduceCancelEditOrder,
  [DELETE_ORDER.start]: reduceDeleteOrderStart,
  [DELETE_ORDER.complete]: reduceDeleteOrderComplete,
  [DELETE_ORDER.failure]: reduceDeleteOrderFailure,
  [EDIT_ORDER]: reduceEditOrder,
  [CHANGE_ORDER]: reduceChangeOrder,
  [CHANGE_SAMPLERS]: reduceChangeSamplers,
  [SAVE_ORDER.start]: reduceSaveOrderStart,
  [SAVE_ORDER.complete]: reduceSaveOrderComplete,
  [SAVE_ORDER.failure]: reduceSaveOrderFailure,
  [SET_ORDER_READY.start]: reduceSetOrderReadyStart,
  [SET_ORDER_READY.failure]: reduceSetOrderReadyFailure,
  [APPROVE_ORDERS.start]: reduceApproveOrdersStart,
  [APPROVE_ORDERS.complete]: reduceApproveOrdersComplete,
  [APPROVE_ORDERS.failure]: reduceApproveOrdersFailure,
  [UNAPPROVE_ORDERS.start]: reduceUnapproveOrdersStart,
  [UNAPPROVE_ORDERS.complete]: reduceUnapproveOrdersComplete,
  [UNAPPROVE_ORDERS.failure]: reduceUnapproveOrdersFailure,
  [FINALIZE_ORDERS.start]: reduceFinalizeOrdersStart,
  [FINALIZE_ORDERS.complete]: reduceFinalizeOrdersComplete,
  [FINALIZE_ORDERS.failure]: reduceFinalizeOrdersFailure,
  [UPDATE_POINT_COUNT]: reduceUpdatePointCount,
};

export const reducers = {
  [SLICE]: (state = {}, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};

function selectAgronomists(state) {
  return state[SLICE].agronomists;
}

function selectSamplers(state) {
  return state[SLICE].domain?.samplers;
}

function selectLaboratories(state) {
  return state[SLICE].domain?.laboratories;
}

function selectSeasons(state) {
  return state[SLICE].domain?.seasons;
}

function selectPriorities(state) {
  return state[SLICE].domain?.priorities;
}

function selectContractTypes(state) {
  return state[SLICE].domain?.contractTypes;
}

function selectOrders(state) {
  return state[SLICE].orders;
}

function selectDashboardStats(state) {
  return state[SLICE].dashboardStats;
}

function selectPoints(state) {
  return state[SLICE].points;
}

function selectDraftOrder(state) {
  return state[SLICE].newOrder?.data;
}

function selectDraftEdit(state) {
  return state[SLICE].editOrder?.data;
}

function selectIsWorking(state) {
  return Boolean(state[SLICE].working);
}

function selectIsLoading(state) {
  return Boolean(state[SLICE].loading);
}

function selectHasFailed(state) {
  return state[SLICE].error;
}

function selectMap(state) {
  return state[SLICE].map || NULL_MAP;
}

function selectScheduledFilter(state) {
  return state[SLICE].scheduledFilter;
}

function createSelectFieldSpecialists() {
  return createSelector(
    (state) => state[SLICE].agronomists || [],
    (agronomistUsers) =>
      agronomistUsers.filter(({ role }) => role === 'fieldSpecialist'),
  );
}

function createSelectSelectedFields() {
  return createSelector(
    (state) => state[SLICE].fields,
    (fields) => fields?.filter((field) => field.isSelected) || [],
  );
}

function createSelectFields() {
  return createSelector(
    (state) => state[SLICE].orders,
    (state) => state[SLICE].fields,
    (currentOrders, currentFields) => {
      if (!currentOrders || !currentFields) return undefined;

      const rowHasActiveOrder = createHasActiveOrder()({
        [SLICE]: { orders: currentOrders },
      });
      const selectableRows = currentFields.filter((field) =>
        isSelectable(rowHasActiveOrder, field),
      );
      const otherRows = currentFields.filter(
        (field) => !isSelectable(rowHasActiveOrder, field),
      );
      return selectableRows.concat(otherRows);
    },
  );
}

function selectSelectOrderIds(state) {
  return state[SLICE].selectedOrders;
}

function createSelectSelectedOrders() {
  return createSelector(
    (state) => state[SLICE].orders,
    (state) => state[SLICE].selectedOrders,
    (orders = [], selected = []) =>
      selected
        .map((id) => orders.find((order) => order.id === id))
        .filter(Boolean),
  );
}

function newOrderIs(is) {
  return (state) => state[SLICE].newOrder?.state === is;
}

function isSelectable(rowHasActiveOrder, row) {
  return !rowHasActiveOrder(row.aaid) && Boolean(row.farmgate.rotation);
}

function selectEditIsWorking(state) {
  return state[SLICE].editOrder?.state === 'working';
}

function selectEditHasFailed(state) {
  return state[SLICE].editOrder?.error;
}

function selectFindOrder(state) {
  return (aaid) =>
    state[SLICE].orders?.find(
      (o) => o.aaid.toUpperCase() === aaid.toUpperCase(),
    );
}

function selectEditPointsIsWorking(state) {
  return state[SLICE].editPoints?.working;
}

function selectEditPointsHasFailed(state) {
  return state[SLICE].editPoints?.error;
}

function selectDeleteHasFailed(state) {
  return state[SLICE].deleteOrder?.state === 'error';
}

function aaidMatcher(aaid, order) {
  return order.aaid.toUpperCase() === aaid.toUpperCase();
}

function ordersForField(allOrders, aaid) {
  return (allOrders ?? []).filter(aaidMatcher.bind(null, aaid));
}

function createHasActiveOrder() {
  return createSelector(
    selectOrders,
    (orders) => (aaid) =>
      ordersForField(orders, aaid).some(
        (order) =>
          !(order.statusId === COMPLETE || order.statusId === CANCELLED),
      ),
  );
}

function createHasSubmittedOrder() {
  return createSelector(
    selectOrders,
    (orders) => (aaid) =>
      ordersForField(orders, aaid).some(
        (order) => order.statusId === SUBMITTED,
      ),
  );
}

function clearFailure() {
  return { type: CLEAR_FAILURE };
}

function setOrderReadyToSample(growerId, id, isReadyToSample) {
  const editedOrder = { id, isReadyToSample };

  return asyncAction(
    SET_ORDER_READY,
    () => service.updateOrder(growerId, editedOrder),
    { startPayload: editedOrder, failurePayload: editedOrder },
  );
}

function deleteOrder(growerId, id) {
  const transformError = (error) => {
    const statusCode = error.isAxiosError && error.response?.status;

    if (statusCode === 405) {
      throw new Error('This order is not able to be cancelled.');
    }

    throw new Error(
      'Error cancelling order. Please try again or contact support if the problem persists.',
    );
  };

  return asyncAction(DELETE_ORDER, () =>
    service
      .deleteOrder(growerId, id)
      .then(() => id)
      .catch(transformError),
  );
}

function setFieldsSelected(selectedRowIds) {
  return { type: SET_FIELDS_SELECTED, payload: selectedRowIds };
}

function setOrdersSelected(selectedRowIds) {
  return { type: SET_ORDERS_SELECTED, payload: selectedRowIds };
}

function setOrders(orders) {
  return { type: SET_ORDERS, payload: orders };
}

function approveOrders(orders) {
  return asyncAction(APPROVE_ORDERS, () =>
    service.approveOrders(orders.map((o) => o.id)),
  );
}

function unapproveOrders(orders) {
  return asyncAction(UNAPPROVE_ORDERS, () =>
    service.unapproveOrders(orders.map((o) => o.id)),
  );
}

function finalizeOrders(orders) {
  return asyncAction(FINALIZE_ORDERS, () =>
    service.finalizeOrders(orders.map((o) => o.id)),
  );
}

function editOrder(order) {
  return { type: EDIT_ORDER, payload: order };
}

function loadDomainData() {
  return asyncAction(LOAD_DOMAIN_DATA, () => service.getOrderDomainData());
}

function cancelOrderEdit() {
  return { type: CANCEL_EDIT_ORDER };
}

function changeOrder(key, value) {
  return { type: CHANGE_ORDER, payload: { key, value } };
}

function changeSamplers(usernames) {
  return { type: CHANGE_SAMPLERS, payload: usernames };
}

function saveOrder(growerId, data) {
  const transformError = (error) => {
    const statusCode = error.isAxiosError && error.response?.status;

    if (statusCode === 405) {
      throw new Error('Cannot edit this order due to its status in SoilView.');
    }

    if (statusCode === 423) {
      throw new Error(
        'SoilView is not allowing updates to this order right now. Please try again later.',
      );
    }

    throw new Error(
      'Error editing order. Please try again or contact support if the problem persists.',
    );
  };
  return asyncAction(SAVE_ORDER, () =>
    service.updateOrder(growerId, data).catch(transformError),
  );
}

function removeFieldFromOrder(field) {
  return { type: REMOVE_NEW_ORDER_FIELD, payload: field.aaid };
}

function loadFields(growerId) {
  return asyncAction(LOAD_FIELDS, () => service.getFields(growerId));
}

function loadAgronomists() {
  return asyncAction(LOAD_AGRONOMISTS, () => getAgronomists());
}

function loadAllOrdersForGrower(growerId) {
  return asyncAction(LOAD_ORDERS, () => service.getOrders({ growerId }), {
    startPayload: { clear: true },
  });
}

function reloadOrder(id) {
  return asyncAction(LOAD_ORDER, () => service.getOrder(id), {
    failurePayload: { id },
  });
}

function unloadOrders() {
  return { type: UNLOAD_ORDERS };
}

function updatePointCount(aaid, updated) {
  return { type: UPDATE_POINT_COUNT, payload: { aaid, ...updated } };
}

function loadActiveOrders() {
  return asyncAction(LOAD_ORDERS, () => service.getOrders());
}

function loadUrgentOrders() {
  return asyncAction(LOAD_ORDERS, () => service.getUrgentOrders());
}

function loadCompletedOrders(year) {
  return asyncAction(LOAD_COMPLETED_ORDERS, (dispatch, getState) => {
    const isAlreadyLoaded = getState().soilSampleOrder?.completedLoaded?.[year];
    if (isAlreadyLoaded) return { year, orders: [] };

    return service
      .getCompletedOrders(year)
      .then((orders) => ({ year, orders }));
  });
}

function addOrder(fields) {
  return { type: ADD_ORDER, payload: fields };
}

function cancelNewOrder() {
  return { type: CANCEL_NEW_ORDER };
}

function setNewOrderData(key, value) {
  return { type: CHANGE_NEW_ORDER, payload: { key, value } };
}

function setNewOrderFieldData(aaid, key, value) {
  return {
    type: CHANGE_NEW_ORDER_FIELD,
    payload: { aaid, key, value },
  };
}

function createOrders(growerId, data) {
  return asyncAction(SAVE_NEW_ORDER, async (dispatch) =>
    service.createOrders(growerId, data).then(async (orders) => {
      dispatch({ type: SET_FIELDS_SELECTED, payload: [] });
      reloadOrder(orders.map(({ id }) => id).join(','))(dispatch);
    }),
  );
}

function reduceClearFailure(state) {
  delete state.error;
  delete state.editPoints?.error;
}

function reduceCancelNewOrder(state) {
  state.newOrder = null;
}

function reduceSaveNewOrderStart(state) {
  state.newOrder = state.newOrder || {};
  Object.assign(state.newOrder, { state: 'working' });
}

function reduceSaveNewOrderComplete(state) {
  state.newOrder = state.newOrder || {};
  Object.assign(state.newOrder, { state: 'complete' });
}

function reduceSaveNewOrderFailure(state, error) {
  state.newOrder = state.newOrder || {};
  Object.assign(state.newOrder, {
    state: 'error',
    error: error.message || error,
  });
}

function reduceStartNewOrder(state, fields) {
  state.newOrder = {
    data: {
      createdBy: '',
      samplerId: INTERNAL_SAMPLER,
      laboratoryId: 1,
      seasonId: '',
      priorityId: '',
      contractType: '',
      fields: fields.map((f) => ({
        aaid: f.aaid,
        crop: f.crop ? f.crop : '',
        notes: '',
        isReadyToSample: false,
      })),
    },
  };
}

function reduceRemoveNewOrderField(state, aaid) {
  state.newOrder.data.fields = state.newOrder.data.fields.filter(
    (f) => f.aaid !== aaid,
  );
}

function reduceChangeNewOrder(state, { key, value }) {
  set(state, `newOrder.data.${key}`, value);
}

function reduceChangeNewOrderField(state, { aaid, key, value }) {
  const targetField = state.newOrder.data.fields.find((f) => f.aaid === aaid);
  if (targetField) targetField[key] = value;
}

function reduceLoadDomainDataComplete(state, payload) {
  state.domain = payload;
}

function reduceLoadFieldsComplete(state, payload) {
  state.fields = payload;
}

function reduceLoadFieldsStart(state) {
  state.fields = null;
}

function reduceLoadAgronomistsComplete(state, payload) {
  state.agronomists = payload.map((agronomist) => ({
    ...agronomist,
    id: agronomist.username,
  }));
}

function reduceLoadAgronomistsStart(state) {
  state.agronomists = null;
}

function reduceUnloadOrders(state) {
  delete state.orders;
  delete state.editOrder;
}

function reduceUpdatePointCount(state, { aaid, points, source }) {
  const pointsUpdater = (target) => {
    if (target.aaid === aaid) {
      target.numberOfPoints = points.length;
      target.sourceOfPoints = source;
    }
  };
  state.fields?.forEach(pointsUpdater);
  state.orders?.forEach(pointsUpdater);
}

function reduceSetSelectedFields(state, selectedIds) {
  state.fields.forEach((field) => {
    field.isSelected = selectedIds.includes(field.id);
  });
}

function startLoading(state) {
  state.loading = state.loading || 0;
  state.loading += 1;
}

function doneLoading(state) {
  state.loading = state.loading || 1;
  state.loading -= 1;
}

function reduceLoadOrderStart(state) {
  startLoading(state);
}

function reduceLoadOrderComplete(state, newOrders) {
  doneLoading(state);
  [newOrders].flat().forEach((newOrder) => {
    const existingOrder = state.orders.find((o) => o.id === newOrder.id);

    if (existingOrder) {
      Object.assign(existingOrder, newOrder);
    } else {
      state.orders.push(newOrder);
    }
  });
}

function reduceLoadOrderFailure(state, payload) {
  doneLoading(state);

  if (payload.error?.response?.status === 404) {
    state.orders = state.orders.filter((o) => o.id !== payload.id);
  }
}

function reduceLoadCompletedOrdersStart(state) {
  startLoading(state);
  state.completedLoaded = state.completedLoaded || {};
}

function reduceLoadCompletedOrdersComplete(state, { year, orders }) {
  doneLoading(state);
  state.completedLoaded = state.completedLoaded || {};
  state.completedLoaded[year] = orders;
  state.orders = uniqBy((state.orders || []).concat(orders), 'id');
}

function reduceLoadCompletedOrdersFailure(state) {
  doneLoading(state);
}

function reduceLoadOrdersStart(state, payload = {}) {
  delete state.error;
  startLoading(state);
  if (payload.clear) delete state.orders;
}

function reduceLoadOrdersComplete(state, payload) {
  doneLoading(state);
  state.orders = uniqBy((state.orders || []).concat(payload), 'id');
}

function reduceLoadOrdersFailure(state, error) {
  state.error = error;
  doneLoading(state);
}

function reduceSetOrders(state, orders) {
  state.orders = orders;
}

function reduceSetSelectedOrders(state, selectedIds) {
  state.selectedOrders = selectedIds;
}

function reduceCancelEditOrder(state) {
  state.editOrder = null;
}

function reduceDeleteOrderStart(state) {
  delete state.error;
  state.working = true;
}

function reduceDeleteOrderComplete(state, orderId) {
  state.working = false;
  state.orders = state.orders.filter((o) => o.id !== orderId);
}

function reduceDeleteOrderFailure(state, error) {
  state.working = false;
  state.error = error.message || error;
}

function reduceEditOrder(state, order) {
  state.editOrder = {
    data: {
      id: order.id,
      createdBy: order.createdBy,
      crop: order.crop,
      growerId: order.growerId,
      growerName: order.growerName,
      fieldName: order.fieldName,
      isAccepted: order.isAccepted,
      isProposed: order.isProposed,
      isReadyToSample: order.isReadyToSample,
      samplerId: order.samplerId,
      samplerUsers: order.samplerUsers,
      seasonId: order.seasonId,
      priorityId: order.priorityId,
      contractType: order.contractType,
      laboratoryId: order.laboratoryId,
      scheduledAt: order.scheduledAt,
      statusDisplay: order.statusDisplay,
      samplerOrderDetails: order.samplerOrderDetails,
      notes: order.notes,
    },
  };
}

function reduceChangeOrder(state, { key, value }) {
  set(state, `editOrder.data.${key}`, value);
}

function reduceChangeSamplers(state, samplers) {
  state.editOrder.data.samplerUsers = samplers.map((username) => ({
    username,
  }));
}

function reduceSaveOrderStart(state) {
  delete state.error;
  state.working = true;
  state.editOrder = state.editOrder || {};
  Object.assign(state.editOrder, { state: 'working' });
}

function reduceSaveOrderComplete(state, editedOrder) {
  state.working = false;
  state.editOrder = null;
  state.orders?.forEach((order) => {
    if (order.id === editedOrder.id) Object.assign(order, editedOrder);
    return order;
  });
}

function reduceSaveOrderFailure(state, error) {
  state.working = false;
  state.editOrder = state.editOrder || {};
  state.editOrder.error = error.message || error;
}

function reduceSetOrderReadyStart(state, editedOrder) {
  state.orders.forEach((order) => {
    if (order.id === editedOrder.id) Object.assign(order, editedOrder);
    return order;
  });
}

function reduceSetOrderReadyFailure(state, editedOrder) {
  state.orders.find(({ id }) => id === editedOrder.id).isReadyToSample =
    !editedOrder.isReadyToSample;
  state.editOrder = { state: 'error' };
}

function reduceApproveOrdersStart(state) {
  delete state.error;
  state.working = true;
}

function reduceApproveOrdersComplete(state, ids) {
  state.working = false;
  state.selectedOrders = state.selectedOrders?.filter(
    (id) => !ids.includes(id),
  );
  state.orders
    .filter((order) => ids.includes(order.id))
    .forEach((order) => {
      order.statusId = 2;
      order.status.name = 'APPROVED';
    });
}

function reduceApproveOrdersFailure(state, error) {
  state.error = error;
  state.working = false;
}

function reduceUnapproveOrdersStart(state) {
  delete state.error;
  state.working = true;
}

function reduceUnapproveOrdersComplete(state, ids) {
  state.working = false;
  state.selectedOrders = state.selectedOrders.filter((id) => !ids.includes(id));
  state.orders
    .filter((order) => ids.includes(order.id))
    .forEach((order) => {
      order.statusId = 1;
      order.status.name = 'AWAITING_APPROVAL';
    });
}

function reduceUnapproveOrdersFailure(state, error) {
  state.error = error;
  state.working = false;
}

function reduceFinalizeOrdersStart(state) {
  delete state.error;
  state.working = true;
}

function reduceFinalizeOrdersComplete(state, ids) {
  state.working = false;
  state.selectedOrders = state.selectedOrders.filter((id) => !ids.includes(id));
  state.orders
    .filter((order) => ids.includes(order.id))
    .forEach((order) => {
      order.statusId = 4;
      order.status.name = 'COMPLETE';
    });
}

function reduceFinalizeOrdersFailure(state, error) {
  state.error = error;
  state.working = false;
}
